import { useTheme } from '@mui/material'
import { GridColDef, GridDensity, GridRowSelectionModel, useGridApiRef } from '@mui/x-data-grid-premium'
import { useQueryClient } from '@tanstack/react-query'
import { useContactsItems } from 'api/aws/contacts/useContactItems'
import { useDeleteMutation } from 'api/aws/useDeleteMutation'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useEntitiesFromSearch } from 'api/search'
import { ContactsGridFooter } from 'components/Contacts/ContactsGridFooter'
import ContactsPopover from 'components/Contacts/ContactsPopover'
import 'components/DataGrid/DataGridContext.css'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import GlobalGridToolbar from 'components/DataGrid/GlobalGridToolbar'
import renderContactsColumns from 'components/DataGrid/createColumns/renderContactsColumns'
import { useTreeExpansionPersistence } from 'components/DataGrid/useTreeExpansionPersistence'
import { gridContainerStyles } from 'components/Organizations/style'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Contact } from 'types'
import { defaultColumnOrder, defaultFilterModel, defaultSortModel, defaultVisibilityModel } from './defaultModels'
import { gridSxProps } from './gridSxProps'

const ContactsPage = () => {
  const _theme = useTheme()
  const { pinned } = useLayoutContext()
  const apiRef = useGridApiRef()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isExpanded, toggleExpanded } = useTreeExpansionPersistence('contacts')
  const [density, setDensity] = useState<GridDensity>(() => {
    const saved = localStorage.getItem('contactsDensityModel') as GridDensity | null
    return saved ?? 'standard'
  })
  const { deals, organizations, isItemsLoading } = useEntitiesFromSearch()
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const { data: rawContacts, isLoading: isContactsLoading } = useContactsItems()

  const contactDealCountMap = useMemo(() => {
    const map = new Map<string, number>()
    deals?.forEach((deal: any) => {
      const seen = new Set<string>()
      deal.contacts?.forEach((contactUuid: string) => {
        seen.add(contactUuid)
      })
      deal.primaryContact?.forEach((contactUuid: string) => {
        seen.add(contactUuid)
      })
      deal.recipients?.contactRecipient?.forEach((r: any) => {
        if (r?.id) seen.add(r.id)
        if (r?.contact_id) seen.add(r.contact_id)
      })
      deal.recipients?.authorizedRecipient?.forEach((r: any) => {
        if (r?.id) seen.add(r.id)
        if (r?.contact_id) seen.add(r.contact_id)
      })
      seen.forEach(uuid => map.set(uuid, (map.get(uuid) ?? 0) + 1))
    })
    return map
  }, [deals])

  const contacts = useMemo(() => {
    if (isContactsLoading || !rawContacts || !users || !organizations || isUsersLoading || isItemsLoading) return []
    return rawContacts?.map((contact: Contact) => {
      const userId = contact && contact?.owner ? contact?.owner?.[0] : null
      const user = users.find(u => u.user_uuid === userId)
      const orgId = contact?.organizations?.[0]
      const relatedOrg = orgId ? organizations?.find(org => org?.org_uuid === orgId) : null
      return {
        rowId: contact?.contacts_uuid,
        ...contact,
        owner: user,
        relatedOrg: relatedOrg,
        dealCount: contactDealCountMap.get(contact?.contacts_uuid) ?? 0,
      }
    })
  }, [rawContacts, organizations, isContactsLoading, isItemsLoading, users, contactDealCountMap])
  const [selectedContact, setSelectedContact] = useState<Contact>(null)
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([])

  const [anchorEl, setAnchorEl] = useState(null)
  const [_isDeleting, setIsDeleting] = useState(false)
  const { mutate: deleteItem } = useDeleteMutation()
  const { showSnackbar } = useSnackbarContext()
  ///////////////////////////////////////////////////////////////////////////\

  const changeRowSelectionModel = (newModel: GridRowSelectionModel) => {
    console.log('newModel', newModel)
    if (Array.from(newModel?.ids)?.length === 1) {
      const selectedRowId = Array.from(newModel?.ids)?.[0]
      const match = (selectedRowId as string).match(
        /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/
      )
      const uuid = match ? match[0] : null
      const newContact = contacts?.find((c: Contact) => c?.contacts_uuid === uuid)
      setSelectedContact(newContact)
    } else if (Array.from(newModel?.ids)?.length > 1) {
      const selectedRowIds = Array.from(newModel?.ids)
      const contactUUIDs = (selectedRowIds as string[])?.flatMap(rowId =>
        rowId.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/)
      )
      const newContacts = contacts?.filter((c: Contact) => contactUUIDs.includes(c?.contacts_uuid))
      setSelectedContact(null)
      setSelectedContacts(newContacts)
    } else {
      setSelectedContact(null)
      setSelectedContacts([])
    }
  }
  const handleDeleteClick = (rowSelectionModel: GridRowSelectionModel) => {
    const selectedRowId = Array.from(rowSelectionModel?.ids)?.[0]
    const match = (selectedRowId as string).match(
      /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/
    )
    const uuid = match ? match[0] : null
    const rowElement = apiRef.current.getRowElement(selectedRowId)
    rowElement.classList.add('delete-row')
    setAnchorEl(rowElement)
    const itemToDelete = contacts?.find(c => c?.contacts_uuid === uuid)
    setSelectedContact(itemToDelete)
  }
  const handleConfirmDelete = async () => {
    setIsDeleting(true)
    if (selectedContact) {
      const params = {
        endpoint: `/aws/contacts/${selectedContact.contacts_uuid}`,
        table: 'contacts',
      }
      deleteItem(params, {
        onSuccess: message => {
          handleClosePopover()
          showSnackbar(message, 'success')
          setIsDeleting(false)
          console.log('invalidating queries: ', `['contacts', ${selectedContact.contacts_uuid}]`)
          queryClient.invalidateQueries({ queryKey: ['contacts', selectedContact.contacts_uuid] })
        },
        onError: error => {
          showSnackbar(error.message, 'error')
          setIsDeleting(false)
        },
      })
    }
  }
  const handleClosePopover = () => {
    setAnchorEl(null)
    apiRef.current.setRowSelectionModel({
      ids: new Set<string>(),
      type: 'include',
    }) // Unselect all rows
    setSelectedContact(null)
  }
  useEffect(() => {
    if (selectedContact) console.log('selectedContact', selectedContact)
    if (selectedContacts?.length) console.log('selectedContacts', selectedContacts)
  }, [selectedContact, selectedContacts])
  const changeDensity = (newDensity: GridDensity) => {
    setDensity(newDensity)
  }

  const handleRowClick = (row: Contact) => {
    const route = `/contacts/${row?.contacts_uuid}/details`
    navigate(route)
  }
  const columns = useMemo(() => {
    return renderContactsColumns(density, handleRowClick) as GridColDef[]
  }, [density])
  const _isLoading = isUsersLoading || isContactsLoading
  const open = Boolean(anchorEl)
  const id = open ? 'contacts-grid-footer-popover' : undefined

  const footerProps = {
    onUpdate: () => {},
    anchorEl,
    setAnchorEl,
    onClickDelete: handleDeleteClick,
    handleConfirmDelete,
    handleClosePopover,
    selectedData: selectedContact,
    setSelectedData: setSelectedContact,
    apiRef,
  }
  return (
    <>
      <GlobalDataGrid
        columns={columns}
        rows={contacts}
        apiRef={apiRef}
        context={'contacts'}
        isExpanded={isExpanded}
        defaultFilterModel={defaultFilterModel}
        defaultVisibilityModel={defaultVisibilityModel}
        defaultSortModel={defaultSortModel}
        defaultGridDensity={'standard'}
        defaultColumnOrder={defaultColumnOrder}
        // aggregationModel={aggregationModel}
        onRowSelectionModelChange={changeRowSelectionModel}
        FooterComponent={ContactsGridFooter}
        footerProps={footerProps}
        onChangeDensity={changeDensity}
        toolbarComponent={GlobalGridToolbar}
        isLoading={isContactsLoading}
        treeData={false}
        gridSxProps={gridSxProps}
        gridContainerSx={{
          position: 'relative',
          ...gridContainerStyles,
          pt: pinned ? '60px' : 0.8,
          transition: 'padding 0.3s ease', // or .5s, etc.
          height: '97%',
          // height: pinned ? 'calc(100vh - 80px)' : 'calc(100vh - 20px)',
        }}
      />
      <ContactsPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        handleConfirmDelete={handleConfirmDelete}
        selectedData={selectedContact}
      />
    </>
  )
}

export default ContactsPage
